/**
 * Kickard PHASE 5.0B-1 — Visual Identity foundation showcase.
 *
 * Internal-only preview at `/design/v5b` that demonstrates every
 * primitive in `components/v5b/` on top of the new backdrops. Used to
 * verify the foundation on real phones before beginning the
 * screen-by-screen migration. This route does NOT link from Home or
 * navigation — it is discoverable only by URL.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  StadiumBackdrop, PitchBackdrop, CompetitiveBackdrop,
  Panel, KKButton, SegmentedTabs,
  RankBadge, FormChip, SRDelta,
  StatTile, ScoreDisplay,
} from '@/components/v5b';

export default function DesignPreviewV5B() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('ranked');

  return (
    <StadiumBackdrop className="min-h-[100dvh] w-full">
      <div className="mx-auto max-w-[430px] px-4 py-6 space-y-5 relative">
        {/* --- Header --- */}
        <div className="flex items-center justify-between">
          <div>
            <div className="kk-type-display text-[hsl(var(--kk-gold-bright))] text-2xl leading-none">
              KICKARD 5.0B-1
            </div>
            <div className="kk-type-ui text-white/50 text-xs mt-1">
              Visual foundation preview · not production UI
            </div>
          </div>
          <KKButton variant="tertiary" onClick={() => navigate('/home')} testId="v5b-close">
            ← Close
          </KKButton>
        </div>

        {/* --- Typography --- */}
        <Panel edgeTop testId="v5b-typography">
          <div className="p-4 space-y-2">
            <div className="kk-type-ui text-[10px] uppercase tracking-[0.28em] text-white/45">
              Typography roles
            </div>
            <div className="kk-type-display text-4xl text-white">DISPLAY · BEBAS</div>
            <div className="kk-type-ui text-sm text-white/80">
              UI · Manrope · Body text at readable size on mobile.
            </div>
            <div className="kk-type-numeric text-lg text-white">
              123 · 45.6% · 0:14 · +23 SR
            </div>
          </div>
        </Panel>

        {/* --- Panels --- */}
        <div className="grid grid-cols-2 gap-3">
          <Panel testId="v5b-panel-default" className="p-3">
            <div className="kk-type-display text-white text-lg">Default</div>
            <div className="kk-type-ui text-white/60 text-xs mt-1">Elevated · subtle</div>
          </Panel>
          <Panel variant="strong" edgeTop testId="v5b-panel-strong" className="p-3">
            <div className="kk-type-display text-white text-lg">Strong</div>
            <div className="kk-type-ui text-white/60 text-xs mt-1">Hero surface</div>
          </Panel>
          <Panel variant="competitive" testId="v5b-panel-comp" className="p-3">
            <div className="kk-type-display text-[hsl(var(--kk-gold-bright))] text-lg">
              Competitive
            </div>
            <div className="kk-type-ui text-white/60 text-xs mt-1">Ranked · cup surfaces</div>
          </Panel>
          <Panel variant="inset" testId="v5b-panel-inset" className="p-3">
            <div className="kk-type-display text-white text-lg">Inset</div>
            <div className="kk-type-ui text-white/60 text-xs mt-1">Inputs · rows</div>
          </Panel>
        </div>

        {/* --- Buttons --- */}
        <Panel testId="v5b-buttons">
          <div className="p-4 space-y-3">
            <div className="kk-type-ui text-[10px] uppercase tracking-[0.28em] text-white/45">
              Button hierarchy
            </div>
            <KKButton variant="primary" className="w-full" testId="v5b-btn-primary">
              Play Ranked
            </KKButton>
            <div className="grid grid-cols-2 gap-2">
              <KKButton variant="secondary" testId="v5b-btn-secondary">
                Home
              </KKButton>
              <KKButton variant="secondary" disabled testId="v5b-btn-secondary-disabled">
                Disabled
              </KKButton>
            </div>
            <div className="flex items-center gap-2">
              <KKButton variant="tertiary" testId="v5b-btn-tertiary">Luck Report →</KKButton>
              <KKButton variant="tertiary" testId="v5b-btn-tertiary-2">Details</KKButton>
            </div>
            <KKButton variant="danger" className="w-full" testId="v5b-btn-danger">
              Reset Stats
            </KKButton>
          </div>
        </Panel>

        {/* --- Segmented tabs --- */}
        <Panel testId="v5b-tabs">
          <div className="p-4 space-y-3">
            <div className="kk-type-ui text-[10px] uppercase tracking-[0.28em] text-white/45">
              Segmented tabs (competitive)
            </div>
            <SegmentedTabs
              testId="v5b-competitive-tabs"
              activeId={tab}
              onSelect={setTab}
              items={[
                { id: 'ranked', label: 'Ranked' },
                { id: 'table', label: 'Table' },
                { id: 'cup', label: 'Weekly Cup' },
              ]}
            />
          </div>
        </Panel>

        {/* --- Competitive primitives --- */}
        <Panel variant="competitive" edgeTop testId="v5b-competitive-block">
          <div className="p-4">
            <div className="kk-type-ui text-[10px] uppercase tracking-[0.28em] text-white/45 mb-3">
              Competitive primitives
            </div>
            <div className="flex items-center gap-3 flex-wrap mb-3">
              <RankBadge tierLabel="Gold II" sr={707} testId="v5b-rankbadge" />
              <SRDelta value={23} testId="v5b-sr-plus" />
              <SRDelta value={-12} testId="v5b-sr-minus" />
              <SRDelta value={0} testId="v5b-sr-zero" />
            </div>
            <div className="flex items-center gap-1.5">
              {['L', 'L', 'W', 'W', 'W'].map((r, i) => (
                <FormChip key={i} result={r} testId={`v5b-form-${i}`} />
              ))}
            </div>
          </div>
        </Panel>

        {/* --- Profile stat tiles --- */}
        <Panel testId="v5b-stat-tiles">
          <div className="p-4 space-y-3">
            <div className="kk-type-ui text-[10px] uppercase tracking-[0.28em] text-white/45">
              Stat tiles
            </div>
            <div className="grid grid-cols-2 gap-2">
              <StatTile label="Matches" value="137" />
              <StatTile label="Win rate" value="55.5%" sub="76W · 61L" />
              <StatTile label="Goals for" value="258" />
              <StatTile label="Clean sheets" value="12" />
            </div>
          </div>
        </Panel>

        {/* --- Score treatment --- */}
        <Panel variant="strong" edgeTop testId="v5b-score">
          <div className="p-4 flex items-center justify-center">
            <ScoreDisplay p1={2} p2={1} testId="v5b-score-display" />
          </div>
        </Panel>

        {/* --- Backdrop previews --- */}
        <div className="space-y-3">
          <PitchBackdrop className="rounded-xl p-4 border border-white/10">
            <div className="kk-type-display text-white text-lg">Pitch backdrop</div>
            <div className="kk-type-ui text-white/60 text-xs">Gameplay surfaces</div>
          </PitchBackdrop>
          <CompetitiveBackdrop className="rounded-xl p-4 border border-white/10">
            <div className="kk-type-display text-[hsl(var(--kk-gold-bright))] text-lg">
              Competitive backdrop
            </div>
            <div className="kk-type-ui text-white/60 text-xs">Ranked / cup / rank reveal</div>
          </CompetitiveBackdrop>
        </div>
      </div>
    </StadiumBackdrop>
  );
}
